import { BlogSchemas } from "@iewc/Types/CustomModels";
import { injectMetadata, removeMetadata } from "@iewc/Utilities/Metadata";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue, MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MobiusPage from "@insite/mobius/Page";
import React, { useEffect } from "react";
import { connect } from "react-redux";

const enum fields {
    publishDate = "publishDate",
    author = "author",
    authorUrl = "authorUrl",
    content = "content",
    thumbnail = "thumbnail",
    summary = "summary",

    displayPublishDate = "Publish date",
    displayAuthor = "Author",
    displayAuthorUrl = "Author External Link",
    displayContent = "Blog content",
    displayThumbnail = "Thumbnail",
    displaySummary = "Summary",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.publishDate]: string;
        [fields.author]: string;
        [fields.authorUrl]: LinkFieldValue;
        [fields.content]: string;
        [fields.thumbnail]: string;
        [fields.summary]: string;
    };
}

export const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const logoWidget = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields as any;

    const { publishDate, author: authorName, content, summary } = ownProps.fields;

    const paragraphs: string[] = [];
    (summary ?? content).replace(/<p>(.*?)<\/p>/g, (str, args) => {
        paragraphs.push(args);
        return str;
    });
    const description = paragraphs[0];

    const author = authorName
        ? {
              "@type": "Person",
              name: authorName,
              url: "",
          }
        : undefined;

    return {
        description,
        author,
        datePublished: publishDate ? `${publishDate}` : undefined,
        logoWidget,
        areLinksLoading: state.links.areLinksLoading,
    };
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const BlogDetailPage = ({
    id,
    name: headline,
    description,
    author,
    datePublished,
    logoWidget,
    areLinksLoading,
    fields: { authorUrl, thumbnail },
}: Props) => {
    const authorUrlLink = useGetLink(authorUrl);

    useEffect(() => {
        if (logoWidget && !areLinksLoading) {
            if (author && authorUrl && authorUrlLink) {
                author.url = authorUrlLink?.url ?? "";
            }

            removeMetadata(BlogSchemas.blogListingWidget);
            removeMetadata(BlogSchemas.blogDetailPage);
            const schemaData = {
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                mainEntityOfPage: {
                    "@type": "WebPage",
                    "@id": window.location.href,
                },
                headline,
                description,
                image: thumbnail,
                author,
                publisher: {
                    "@type": "Organization",
                    name: "IEWC",
                    logo: {
                        "@type": "ImageObject",
                        url: logoWidget.logoImage ?? "",
                    },
                },
                datePublished,
            };
            injectMetadata(JSON.stringify(schemaData), BlogSchemas.blogDetailPage);
        }
    }, [logoWidget, areLinksLoading]);

    return (
        <MobiusPage>
            <Zone contentId={id as never} zoneName={"Content" as never} requireRows={true as never} />
        </MobiusPage>
    );
};
const pageModule: PageModule = {
    component: connect(mapStateToProps)(BlogDetailPage as any) as any,
    definition: {
        displayName: "Blog Detail",
        pageType: "Content",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        allowedParents: ["BlogCategoryPage" as any],
        allowedChildren: [],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            {
                name: fields.publishDate,
                displayName: fields.displayPublishDate,
                editorTemplate: "DateTimeField",
                defaultValue: null,
                fieldType: "General",
            },
            {
                name: fields.author,
                displayName: fields.displayAuthor,
                editorTemplate: "SystemListDropDownField",
                systemListName: "Authors",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.authorUrl,
                displayName: fields.displayAuthorUrl,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isVisible: item => item.fields.author != null && item.fields.author !== "",
            },
            {
                name: fields.content,
                displayName: fields.displayContent,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                collapsedToolbarButtons: {
                    moreText: {
                        buttons: ["paragraphFormat", "bold", "italic", "html"],
                        buttonsVisible: 4,
                    },
                },
                extendedConfig: {
                    imageEditButtons: ["imageDisplay", "imageAlign", "imageRemove"],
                },
            },
            {
                name: fields.thumbnail,
                displayName: fields.displayThumbnail,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
            },
            {
                name: fields.summary,
                displayName: fields.displaySummary,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default pageModule;
